import { useState, useEffect, useEffectEvent } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import StepGuide from '../test/StepGuide';
import { getLevelProtocol } from '../../utils/protocol';
import { LEVEL_ENTRY_WINDOW_SECONDS } from '../../utils/timedLevel';
import { cancelSpeech, speakText } from '../../utils/voiceCoach';

interface PreLevelScreenProps {
  level: number;
  countdownSeconds: number;
  onComplete: () => void;
  playCountBeep: (isLast: boolean) => void;
}

function speakExplanation() {
  speakText(
    'Welcome to the Chester Step Test. ' +
    'You will step up and down to a metronome beat. The pattern is: left up, right up, left down, right down. ' +
    'Each level lasts 2 minutes, and the pace increases each level. ' +
    `During the final ${LEVEL_ENTRY_WINDOW_SECONDS} seconds, check your heart rate and record it. ` +
    'Press the button when you are ready to begin.',
  );
}

function LevelCountdown({ level, countdownSeconds, onComplete, playCountBeep }: PreLevelScreenProps) {
  const [count, setCount] = useState(countdownSeconds);
  const proto = getLevelProtocol(level);
  const completeCountdown = useEffectEvent(() => onComplete());
  const playCountdownBeep = useEffectEvent((isLast: boolean) => playCountBeep(isLast));

  useEffect(() => {
    let remaining = countdownSeconds;
    const interval = window.setInterval(() => {
      remaining = Math.max(0, remaining - 1);
      setCount(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        completeCountdown();
      } else {
        playCountdownBeep(remaining <= 1);
      }
    }, 1000);

    playCountdownBeep(countdownSeconds <= 1);
    return () => clearInterval(interval);
  }, [countdownSeconds]);

  return (
    <div className="flex flex-col items-center px-6" style={{ flex: 1 }}>
      <div style={{ flex: 1 }} />

      <Badge>Level {level} of 5</Badge>

      <p className="font-mono" style={{ fontSize: '0.875rem', color: 'var(--text2)', marginTop: '24px', marginBottom: '12px' }}>
        {proto.spm} steps/min — {proto.bpm} BPM
      </p>

      <div className="relative my-8">
        <span
          className="font-serif tabular-nums transition-transform duration-200"
          style={{
            fontSize: '140px',
            lineHeight: 1,
            color: 'var(--text)',
            transform: `scale(${count <= 3 ? 1.1 : 1})`,
          }}
        >
          {count}
        </span>
      </div>

      <p className="font-mono" style={{ fontSize: '0.875rem', color: 'var(--text2)', marginBottom: '32px' }}>
        {level === 1 ? 'Starting...' : 'Next level starting...'}
      </p>

      <div style={{ flex: 0.3 }} />

      <div className="w-full flex justify-center" style={{ paddingBottom: '40px' }}>
        <StepGuide bpm={proto.bpm} />
      </div>
    </div>
  );
}

export default function PreLevelScreen(props: PreLevelScreenProps) {
  return <PreLevelSession key={props.level} {...props} />;
}

function PreLevelSession({ level, countdownSeconds, onComplete, playCountBeep }: PreLevelScreenProps) {
  const [phase, setPhase] = useState<'explain' | 'countdown'>(level === 1 ? 'explain' : 'countdown');
  const proto = getLevelProtocol(level);

  useEffect(() => {
    if (level === 1) {
      speakExplanation();
    }
    return () => cancelSpeech();
  }, [level]);

  function handleReady() {
    cancelSpeech();
    setPhase('countdown');
  }

  if (phase === 'countdown') {
    return (
      <LevelCountdown
        key={countdownSeconds}
        level={level}
        countdownSeconds={countdownSeconds}
        onComplete={onComplete}
        playCountBeep={playCountBeep}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, padding: '24px 24px 32px' }}>
      <div style={{ textAlign: 'center', width: '100%', marginBottom: '24px', marginTop: '12px' }}>
        <Badge>Level 1 of 5</Badge>
        <h2
          className="font-serif"
          style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginTop: '16px', marginBottom: '8px' }}
        >
          How the Test Works
        </h2>
        <p className="font-mono" style={{ fontSize: '0.65rem', color: 'var(--text2)', lineHeight: 1.5 }}>
          {proto.spm} steps/min · {proto.bpm} BPM · 2 minutes per level
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', marginBottom: '24px' }}>
        <div style={{ width: '100%', maxWidth: '360px' }}>
          <StepGuide bpm={proto.bpm} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px', width: '100%', maxWidth: '360px' }}>
        {[
          {
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
              </svg>
            ),
            text: 'Step to the metronome beat: Left up, Right up, Left down, Right down.',
          },
          {
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            ),
            text: 'The pace increases every 2 minutes. Most people complete 3–4 levels.',
          },
          {
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            ),
            text: `At ${LEVEL_ENTRY_WINDOW_SECONDS} seconds remaining, check your heart rate and enter it when prompted.`,
          },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, marginTop: '2px', color: 'var(--accent)' }}>{item.icon}</div>
            <p className="font-mono" style={{ fontSize: '0.72rem', color: 'var(--text)', lineHeight: 1.6 }}>
              {item.text}
            </p>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 'auto', width: '100%' }}>
        <Button onClick={handleReady}>I'm Ready — Start Level 1</Button>
      </div>
    </div>
  );
}
